import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { talentAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  ArrowLeft, Star, Building2, MapPin, Calendar, Loader2,
  CheckCircle2, Clock, ThumbsUp, ThumbsDown
} from 'lucide-react';
import { toast } from 'sonner';

import { formatDateMonthDay } from '../../utils/timeFormat';
import RatingFlow from '../../components/RatingFlow';
const StarRating = ({ value, onChange, label }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div>
      <Label className="text-sm text-gray-700 mb-1.5 block">{label}</Label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            className="p-0.5 transition-transform hover:scale-110"
          >
            <Star 
              className={`w-7 h-7 ${
                star <= (hover || value) 
                  ? 'text-amber-400 fill-amber-400' 
                  : 'text-gray-200'
              }`} 
            />
          </button>
        ))}
      </div>
    </div>
  );
};

const TalentRatings = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingRatings, setPendingRatings] = useState([]);
  const [givenRatings, setGivenRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Rating modal state
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [clientRating, setClientRating] = useState(0);
  const [clientFeedback, setClientFeedback] = useState('');
  const [gigRating, setGigRating] = useState(0);
  const [gigFeedback, setGigFeedback] = useState('');
  const [wouldWorkAgain, setWouldWorkAgain] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Iter-279 — read-only detail modal for a rating you already gave.
  const [detailRating, setDetailRating] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pendingRes, givenRes] = await Promise.all([
        talentAPI.getPendingRatings(),
        talentAPI.getRatingsGiven()
      ]);
      setPendingRatings(pendingRes.data);
      setGivenRatings(givenRes.data);
    } catch (e) {
      toast.error('Failed to load ratings');
    } finally {
      setLoading(false);
    }
  };

  const openRatingModal = (booking) => {
    setSelectedBooking(booking);
    setShowRatingModal(true);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star key={i} className={`w-4 h-4 ${i < rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`} />
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="talent-ratings-page">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/talent/dashboard')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-lg font-semibold text-gray-900">Rate Assignment &amp; Client</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-gray-100">
            <TabsTrigger value="pending" className="data-[state=active]:bg-white">
              <Clock className="w-4 h-4 mr-1.5" />
              Pending ({pendingRatings.length})
            </TabsTrigger>
            <TabsTrigger value="given" className="data-[state=active]:bg-white">
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              Given ({givenRatings.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            {pendingRatings.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No pending ratings</p>
                  <p className="text-sm text-gray-400 mt-1">Complete gigs to rate clients</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingRatings.map(booking => (
                  <Card
                    key={booking.booking_id}
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    data-testid="pending-rating-card"
                    onClick={(e) => { if (e.target.closest('button,a,[role="button"]')) return; openRatingModal(booking); }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Building2 className="w-4 h-4 text-cyan-500" />
                            <h3 className="font-semibold text-gray-900">{booking.company_name}</h3>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{booking.venue_name}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5" />
                              {booking.city}, {booking.state}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {formatDateMonthDay(booking.shift_date)}
                            </span>
                          </div>
                          {booking.hours_worked > 0 && (
                            <Badge variant="outline" className="mt-2 text-xs">
                              {booking.hours_worked} hours worked
                            </Badge>
                          )}
                        </div>
                        <Button
                          onClick={() => openRatingModal(booking)}
                          className="bg-cyan-500 hover:bg-cyan-600"
                          data-testid="rate-btn"
                        >
                          <Star className="w-4 h-4 mr-1.5" />
                          Rate Gig
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="given">
            {givenRatings.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Star className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No ratings given yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {givenRatings.map(rating => (
                  <Card
                    key={rating.id}
                    data-testid={`given-rating-card-${rating.id}`}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={(e) => { if (e.target.closest('button,a,[role="button"]')) return; setDetailRating(rating); }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Building2 className="w-4 h-4 text-cyan-500" />
                            <h3 className="font-semibold text-gray-900">{rating.company_name}</h3>
                          </div>
                          <p className="text-sm text-gray-600">{rating.venue_name}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                            <span>{rating.city}, {rating.state}</span>
                            <span>{formatDateMonthDay(rating.shift_date)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {rating.would_work_again ? (
                            <Badge className="bg-green-100 text-green-700 border-green-200">
                              <ThumbsUp className="w-3 h-3 mr-1" />Would work again
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700 border-red-200">
                              <ThumbsDown className="w-3 h-3 mr-1" />Would not work again
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-100">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Client Rating</p>
                          <div className="flex items-center gap-1">
                            {renderStars(rating.client_rating)}
                            <span className="text-sm font-medium ml-1">{rating.client_rating}</span>
                          </div>
                          {rating.client_feedback && (
                            <p className="text-sm text-gray-600 mt-1.5">{rating.client_feedback}</p>
                          )}
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Gig Experience</p>
                          <div className="flex items-center gap-1">
                            {renderStars(rating.gig_rating)}
                            <span className="text-sm font-medium ml-1">{rating.gig_rating}</span>
                          </div>
                          {rating.gig_feedback && (
                            <p className="text-sm text-gray-600 mt-1.5">{rating.gig_feedback}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Rating Modal — Iter-163 multi-category flow.
          Replaces the legacy "Rate iEvntür" surface entirely. */}
      <RatingFlow
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        booking={selectedBooking || {}}
        ratingType="talent_to_client"
        onRatingSubmitted={fetchData}
      />

      {/* Iter-279 — Read-only detail modal for a rating you already gave. */}
      <Dialog open={!!detailRating} onOpenChange={(o) => !o && setDetailRating(null)}>
        <DialogContent data-testid="given-rating-detail-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Your rating</DialogTitle>
            <DialogDescription className="sr-only">Read-only view of a rating you submitted.</DialogDescription>
          </DialogHeader>
          {detailRating && (
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="w-4 h-4 text-cyan-500" />
                  <h3 className="font-semibold text-slate-900" data-testid="detail-company-name">{detailRating.company_name}</h3>
                </div>
                <p className="text-sm text-slate-600" data-testid="detail-venue">{detailRating.venue_name}</p>
                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mt-1">
                  {(detailRating.city || detailRating.state) && (
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{detailRating.city}{detailRating.state ? `, ${detailRating.state}` : ''}</span>
                  )}
                  <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />Event {formatDateMonthDay(detailRating.shift_date)}</span>
                  {detailRating.created_at && (
                    <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" />Submitted {formatDateMonthDay(detailRating.created_at)}</span>
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-semibold text-slate-700">Client rating</p>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className={`w-4 h-4 ${i <= (detailRating.client_rating || 0) ? 'text-yellow-400 fill-current' : 'text-slate-300'}`} />
                    ))}
                    <span className="text-xs text-slate-600 ml-1">{detailRating.client_rating || 0}/5</span>
                  </div>
                </div>
                {detailRating.client_feedback ? (
                  <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="detail-client-feedback">{detailRating.client_feedback}</p>
                ) : (
                  <p className="text-xs italic text-slate-400">No written feedback.</p>
                )}
              </div>

              <div className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-semibold text-slate-700">Gig experience</p>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className={`w-4 h-4 ${i <= (detailRating.gig_rating || 0) ? 'text-yellow-400 fill-current' : 'text-slate-300'}`} />
                    ))}
                    <span className="text-xs text-slate-600 ml-1">{detailRating.gig_rating || 0}/5</span>
                  </div>
                </div>
                {detailRating.gig_feedback ? (
                  <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="detail-gig-feedback">{detailRating.gig_feedback}</p>
                ) : (
                  <p className="text-xs italic text-slate-400">No written feedback.</p>
                )}
              </div>

              <div>
                {detailRating.would_work_again ? (
                  <Badge className="bg-green-100 text-green-700 border-green-200">
                    <ThumbsUp className="w-3 h-3 mr-1" />Would work again
                  </Badge>
                ) : (
                  <Badge className="bg-red-100 text-red-700 border-red-200">
                    <ThumbsDown className="w-3 h-3 mr-1" />Would not work again
                  </Badge>
                )}
              </div>

              <p className="text-[11px] text-slate-400 italic">
                Ratings are read-only after submission. If you need to correct one, contact support.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setDetailRating(null)} data-testid="given-rating-detail-close" className="bg-cyan-600 hover:bg-cyan-700 text-white">Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TalentRatings;
